/**
 * Clinical encounter client (Podiatry workbench, 2026-10-27). Mirrors backend/src/routes/clinicalEncounters.js; the
 * wire shapes are in ./types.ts. This module and the components beside it are the only frontend code that names the
 * /clinical endpoints.
 *
 * `api` RESOLVES on a refusal (`{success:false,status,code,message,detail?}`) and carries neither status nor code when
 * the server was never reached; `unanswered` is that test. Nothing here stores clinical data in the browser.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../../services/api";
import { AUTH_TOKEN_CHANGED, readImpersonation } from "../../../../services/impersonation";
import type {
  ClinicalResult,
  Encounter,
  EncounterAddendum,
  EncounterStatus,
  Issue,
  IssueReport,
  PatientEncounters,
  PracticeStatus,
  PracticesResponse,
  RegisteredTemplate,
  TemplateApproval,
} from "./types";

const BASE = "/clinical";
const enc = (v: string) => encodeURIComponent(v);
const withKey = (key: string) => ({ headers: { "Idempotency-Key": key } });

/** One key per attempt: a retry with the same key is the same change (replayed), never a second one. */
export function newClinicalKey(): string {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `cx-${c.randomUUID()}`;
  return `cx-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * True when the answer never arrived (or was lost): keep the key and let the provider try again. A success carries
 * neither a status nor a code either, so it is excluded explicitly.
 */
export const unanswered = (r: { success?: boolean; status?: number; code?: string }) =>
  !r.success && r.status === undefined && !r.code;

/**
 * One Idempotency-Key PER ACTION, held while that action's answer is unknown and dropped once the server answers it.
 * A lost answer retries the same action with the same key (applied once); a different next action never reuses it.
 * (providerStockApi.useActionKeys is the same idea, but it mints "ps-" keys for the stock routes.)
 */
export function useActionKeys() {
  const keys = useRef<Record<string, string>>({});
  const keyFor = useCallback((action: string) => {
    if (!keys.current[action]) keys.current[action] = newClinicalKey();
    return keys.current[action];
  }, []);
  const settle = useCallback((action: string) => {
    delete keys.current[action];
  }, []);
  return { keyFor, settle };
}

/**
 * Who is being viewed, when this page is a read-only "Sign in as" (docs/security/ADMIN_SIGN_IN_AS.md, ruling
 * 2026-10-29): the name, or null in the person's own session. Read from the stored token, like the banner, and
 * re-read when the token changes. While it is set nothing here may start, save, submit, sign or add an addendum: the
 * server refuses every such write (403 IMPERSONATION_READ_ONLY), and the screens do not offer one.
 */
const viewedName = (): string | null => {
  const c = readImpersonation();
  return c ? c.targetName || "this person" : null;
};

export function useViewingAs(): string | null {
  const [name, setName] = useState<string | null>(viewedName);
  useEffect(() => {
    const sync = () => setName(viewedName());
    window.addEventListener(AUTH_TOKEN_CHANGED, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(AUTH_TOKEN_CHANGED, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return name;
}

/** The sentence a viewing session shows wherever a write would be. */
export const viewingWords = (name: string) =>
  `Viewing as ${name} — read-only. Nothing can be started, saved, submitted or signed while viewing.`;

const call = async <T>(p: Promise<unknown>): Promise<ClinicalResult<T>> => (await p) as ClinicalResult<T>;

export const clinicalApi = {
  practices: (facilityId: string) =>
    call<PracticesResponse>(api.get(`${BASE}/practices?facilityId=${enc(facilityId)}`)),

  template: (templateVersionId: string) =>
    // The server answers the registered template itself at `data` (routes/clinicalEncounters.js).
    call<RegisteredTemplate>(api.get(`${BASE}/templates/${enc(templateVersionId)}`)),

  /** The practice's current registered template (a GET): the form a viewing session reviews without a draft. */
  currentTemplate: (practice: "PODIATRY") =>
    call<RegisteredTemplate>(api.get(`${BASE}/practices/${enc(practice)}/template`)),

  patientEncounters: (patientId: string, facilityId: string) =>
    call<PatientEncounters>(api.get(`${BASE}/patients/${enc(patientId)}/encounters?facilityId=${enc(facilityId)}`)),

  encounter: (id: string) => call<{ encounter: Encounter }>(api.get(`${BASE}/encounters/${enc(id)}`)),

  create: (patientId: string, facilityId: string, key: string) =>
    call<{ encounter: Encounter; replayed: boolean; existing?: boolean }>(
      api.post(
        `${BASE}/encounters`,
        { patient_id: patientId, facility_id: facilityId, practice: "PODIATRY" },
        withKey(key),
      ),
    ),

  save: (id: string, revision: number, answers: unknown) =>
    call<{ encounter: Encounter }>(api.patch(`${BASE}/encounters/${enc(id)}`, { revision, answers })),

  submit: (id: string, revision: number) =>
    call<{ encounter: Encounter }>(api.post(`${BASE}/encounters/${enc(id)}/submit`, { revision })),

  sign: (id: string, revision: number, key: string) =>
    call<{ encounter: Encounter; replayed: boolean }>(
      api.post(`${BASE}/encounters/${enc(id)}/sign`, { revision, attest: true }, withKey(key)),
    ),

  addendum: (id: string, body: string, key: string) =>
    // The server answers the whole encounter (addenda included), so the screen shows exactly what was recorded.
    call<{ encounter: Encounter; replayed: boolean; addendum_id: string }>(
      api.post(`${BASE}/encounters/${enc(id)}/addenda`, { body }, withKey(key)),
    ),
};

/* ----------------------------------------------------------------- words */

export const STATUS_WORDS: Record<EncounterStatus, string> = {
  DRAFT: "Draft",
  READY_FOR_SIGNATURE: "Ready to sign",
  SIGNED: "Signed",
};

export const PRACTICE_STATUS_WORDS: Record<PracticeStatus, string> = {
  ACTIVE: "available",
  NOT_AUTHORIZED: "not authorized for you",
  NOT_AT_FACILITY: "not offered at this building",
  NOT_OPEN: "not open yet",
  NOT_AVAILABLE: "not available",
};

export const UNREACHED_WORDS = "eMerge did not confirm";

/** Template line for the header: the version, and the approval state while it is not APPROVED. */
export function templateWords(version: number, approval: TemplateApproval): string {
  if (approval === "APPROVED") return `Podiatry v${version}`;
  if (approval === "RETIRED") return `Podiatry v${version} · retired template`;
  return `Podiatry v${version} · pending clinical approval`;
}

/** Why signing is not offered, in words. Unknown reasons are said as unknown, never as a reassurance. */
export function signBlockedWords(reason: string | null | undefined): string {
  switch (reason) {
    case "TEMPLATE_NOT_APPROVED":
      return "Signing opens when the clinical owner approves this template";
    case "NOT_AUTHOR":
    case "NOT_AUTHORIZED":
      return "Only the provider who documented this encounter, with Podiatry authorization, can sign it";
    case "WRITES_CLOSED":
      return "Podiatry documentation is not open yet, so nothing can be signed";
    case "NOT_READY":
    case "NOT_SUBMITTED":
    case "NOT_READY_FOR_SIGNATURE":
      return "Review the encounter for signature first";
    case "ALREADY_SIGNED":
    case "ENCOUNTER_SIGNED":
      return "This encounter is already signed";
    case "RULES_NOT_AVAILABLE":
      return "eMerge has no clinical rules for this template version, so it cannot be signed";
    case "NOT_AT_FACILITY":
      return "Podiatry is not offered at this building, or you cannot document there";
    case "NOT_CARE_RELATED":
      return "You no longer have a care relationship with this resident at this building";
    case "PATIENT_NOT_AT_FACILITY":
      return "The resident is no longer placed at this building";
    default:
      return reason ? `Signing is not available (${reason.toLowerCase().replace(/_/g, " ")})` : "Signing is not available";
  }
}

/** Why a new encounter cannot be started, in words. */
export function startBlockedWords(reason: string | null | undefined): string {
  switch (reason) {
    case "NOT_AUTHORIZED":
      return "You are not authorized to document Podiatry.";
    case "NOT_AT_FACILITY":
      return "Podiatry is not offered at this building.";
    case "WRITES_CLOSED":
    case "NOT_OPEN":
      return "Podiatry documentation is not open yet.";
    case "OPEN_ENCOUNTER_EXISTS":
    case "OPEN_ENCOUNTER":
      return "You already have an unsigned podiatry encounter for this resident.";
    case "NOT_CARE_ASSIGNED":
    case "NOT_CARE_RELATED":
      return "Podiatry encounters start from residents you are assigned to.";
    case "PATIENT_NOT_AT_FACILITY":
      return "This resident is not placed at this building.";
    case "FACILITY_REQUIRED":
      return "Open the resident from a building to start an encounter.";
    case "TEMPLATE_NOT_REGISTERED":
      return "The Podiatry template is not registered on this server yet.";
    case "TEMPLATE_RETIRED":
      return "The Podiatry template in use has been retired; a new version must be published.";
    case "NOT_A_PROVIDER":
      return "Your login is not linked to an active provider record.";
    default:
      return reason
        ? `A podiatry encounter cannot be started here (${reason.toLowerCase().replace(/_/g, " ")}).`
        : "A podiatry encounter cannot be started here.";
  }
}

/** The server's refusal for a read or write, in words (permission, scope, closed gate and others stay apart). */
export function refusalWords(r: ClinicalResult<unknown>, doing: string): string {
  if (unanswered(r)) return `Could not reach eMerge to ${doing}.`;
  if (r.status === 403) return `You are not allowed to ${doing}.`;
  if (r.status === 404) return `eMerge could not find this, or Podiatry documentation is not open: could not ${doing}.`;
  return r.message || r.error || `Could not ${doing}.`;
}

/* ---------------------------------------------------------------- shapes */

const isArr = (v: unknown): v is unknown[] => Array.isArray(v);
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !isArr(v);

const asIssues = (v: unknown): Issue[] =>
  isArr(v)
    ? v
        .filter(isObj)
        .map((i) => ({ path: String(i.path ?? ""), code: String(i.code ?? ""), message: String(i.message ?? "") }))
    : [];

/** `detail.errors` / `detail.blocking` from a 422, checked rather than assumed. */
export function detailIssues(detail: Record<string, unknown> | undefined, key: "errors" | "blocking"): Issue[] {
  return asIssues(detail?.[key]);
}

export function currentRevisionOf(detail: Record<string, unknown> | undefined): number | null {
  const n = detail?.current_revision;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

export const emptyIssues = (): IssueReport => ({ errors: [], blocking: [], review: [] });

/** A server encounter that can be trusted to render (the shape is checked, never assumed). */
export function isEncounter(v: unknown): v is Encounter {
  return (
    isObj(v) &&
    typeof v.id === "string" &&
    typeof v.revision === "number" &&
    typeof v.status === "string" &&
    isObj(v.template) &&
    typeof (v.template as Record<string, unknown>).id === "string"
  );
}

export function isTemplate(v: unknown): v is RegisteredTemplate {
  return isObj(v) && isObj(v.definition) && isArr((v.definition as Record<string, unknown>).sections);
}

/** The encounter's issue report, defaulted so a partial server answer cannot crash the workbench. */
export function issuesOf(e: Encounter): IssueReport {
  const i = (e.issues || {}) as Partial<IssueReport>;
  return { errors: asIssues(i.errors), blocking: asIssues(i.blocking), review: asIssues(i.review) };
}

/* ----------------------------------------------------------------- dates */

/** "Oct 27, 2026" from a YYYY-MM-DD visit day (no time-zone conversion: it is a calendar day, not an instant). */
export function dayWords(day: string | null | undefined): string {
  if (!day) return "date not recorded";
  const t = new Date(`${day.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(t.getTime())) return day;
  return t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function whenWords(iso: string | null | undefined): string {
  if (!iso) return "time not recorded";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "time not recorded";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function clockWords(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  const t = Number.isNaN(d.getTime()) ? new Date() : d;
  return t.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
